import i18next, { type i18n } from 'i18next';
import { DEFAULT_LOCALE, RESOURCES, type TLocale, type TTranslationKey } from '@/i18n/config';

export type TTranslationParams = Record<string, string | number>;

export type TTranslate = (key: TTranslationKey, params?: TTranslationParams) => string;

// One i18next instance per language. The resources never change at runtime, so they are
// built once and shared by every request (server) or render (client).
const instances = new Map<TLocale, i18n>();

const instanceFor = (locale: TLocale) => {
  let instance = instances.get(locale);
  if (!instance) {
    instance = i18next.createInstance();
    // The same settings as the app's @imko/translation, so a key behaves the same in both.
    instance.init({
      resources: RESOURCES,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      compatibilityJSON: 'v3',
      returnEmptyString: true,
      interpolation: { escapeValue: false },
      // Resources are bundled, so there is nothing to wait for — init synchronously.
      initImmediate: false,
    });
    instances.set(locale, instance);
  }
  return instance;
};

/**
 * `t(key, params)` with the app's `{param}` placeholders (its TranslatedText replaces
 * `{name}`, not i18next's `{{name}}`), so the locale files read the same in both projects.
 */
export const createTranslator = (locale: TLocale): TTranslate => {
  const instance = instanceFor(locale);
  return (key, params) => {
    let text = instance.t(key);
    if (params) {
      for (const [name, value] of Object.entries(params)) text = text.replaceAll(`{${name}}`, String(value));
    }
    return text;
  };
};
